/**
 * Operating plan + bets + brief (spec #45 §6), read-only in this step.
 *
 * NOT `plansApi.ts` — that one is billing plans. This is the tenant's
 * OPERATING plan: the goal, the lanes, and the bets the effect lane and
 * the effect rail render.
 *
 * `GET /api/plan` answers `200 {status:"none"}` for a tenant with no
 * approved plan, so callers get an honest empty state instead of an error
 * to swallow. `getPlan` therefore does NOT translate a 404 into `none`:
 * a 404 means the endpoint is missing (a backend that has not landed
 * yet), which is a different fact and the caller renders it differently.
 */

import { createHeaders } from './apiClient';
import { handleResponse } from './httpResponse';
import type {
  Bet,
  BriefResponse,
  PlanResponse,
  PlanVersionDetail,
  PlanVersionSummary,
} from '../types/plan';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

export async function getPlan(): Promise<PlanResponse> {
  const response = await fetch(`${API_BASE_URL}/api/plan`, {
    method: 'GET',
    headers: createHeaders(),
  });
  return handleResponse<PlanResponse>(response);
}

/**
 * The tenant's bets, optionally narrowed to one owner domain (#45 §6's
 * optional companion to delta 3).
 *
 * The envelope is normalised here because #45 §6 writes the bet SHAPE
 * without pinning whether the list arrives bare or wrapped. Accepting
 * both is one line and keeps a shape difference from blanking the effect
 * lane; the fields inside are pinned by the contract test either way.
 */
export async function getPlanBets(ownerDomain?: string): Promise<Bet[]> {
  const qs = ownerDomain
    ? `?owner_domain=${encodeURIComponent(ownerDomain)}`
    : '';
  const response = await fetch(`${API_BASE_URL}/api/plan/bets${qs}`, {
    method: 'GET',
    headers: createHeaders(),
  });
  const body = await handleResponse<
    Bet[] | { bets?: Bet[]; items?: Bet[] } | undefined
  >(response);
  if (!body) return [];
  if (Array.isArray(body)) return body;
  return body.bets ?? body.items ?? [];
}

export async function getBrief(): Promise<BriefResponse> {
  const response = await fetch(`${API_BASE_URL}/api/brief`, {
    method: 'GET',
    headers: createHeaders(),
  });
  return handleResponse<BriefResponse>(response);
}

/**
 * Every plan version, newest first. The store is append-only, so this IS
 * the history — and the DRAFT the interview just produced is in it, which
 * is how the terminal card finds a document to render before anything is
 * in force.
 */
export async function listPlanVersions(): Promise<PlanVersionSummary[]> {
  const response = await fetch(`${API_BASE_URL}/api/plan/versions`, {
    method: 'GET',
    headers: createHeaders(),
  });
  const body = await handleResponse<{ versions?: PlanVersionSummary[] }>(
    response
  );
  return body?.versions ?? [];
}

/** One version's full document, optionally diffed against an earlier one. */
export async function getPlanVersion(
  version: number,
  diffFrom?: number
): Promise<PlanVersionDetail> {
  const qs = diffFrom != null ? `?diff_from=${diffFrom}` : '';
  const response = await fetch(
    `${API_BASE_URL}/api/plan/versions/${version}${qs}`,
    { method: 'GET', headers: createHeaders() }
  );
  return handleResponse<PlanVersionDetail>(response);
}
